import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { Job } from '~/common/entities/Project/ProjectTypes';
import { IdProvider, unwrap } from '../common/apiCommons';
import { ImageType } from 'react-images-uploading';
import axios, { AxiosPromise } from 'axios';

export enum JobPictureKeys {
  JOB_PICTURE = 'JOB_PICTURE',
  UNACCEPTED_COUNT = 'UNACCEPTED_COUNT',
  UNACCEPTED_PICTURE_JOBS = 'UNACCEPTED_PICTURE_JOBS',
  RAW_JOB_PICTURES = 'RAW_JOB_PICTURES',
}

export interface Picture {
  id: number;
  rotation: 0 | 90 | 180 | 270;
  picture: string;
}

export interface JobPicture {
  id: number;
  order_number: number;
  accepted: boolean;
}

export interface MissingPicJob {
  id: number;
  employee: number;
  project: number;
  pictures: JobPicture[];
  name: string;
  description: string;
  done_per_project: number;
  duration: number;
  department: number;
}

const queries = {
  async getPicturesOfJob(job: IdProvider) {
    if (!job?.id) {
      return null;
    }
    const res = await axios.get<JobPicture[]>(`/images/job/of/${job.id}/`);
    return unwrap(res);
  },
  async getUnacceptedPictureCount() {
    const res = await axios.get<{ count: number }>(
      '/images/job/unaccepted/count/',
    );
    return unwrap(res);
  },
  async getJobsWithUnacceptedPictures(
    page: number,
    filterState: { employee?: number; project?: number },
  ): Promise<MissingPicJob[]> {
    let queryParams = `?page=${page}`;
    if (filterState && filterState.employee) {
      queryParams += `&employee=${filterState.employee}`;
    }
    if (filterState && filterState.project) {
      queryParams += `&project=${filterState.project}`;
    }
    const res = await axios.get<MissingPicJob[]>(
      `/images/job/unaccepted/${queryParams}`,
    );
    return unwrap(res);
  },
  async getRawJobPicture(jobPic: IdProvider) {
    if (!jobPic?.id) {
      return null;
    }
    return axios.get<Picture>(`/job/pic/${jobPic.id}/`).then(unwrap);
  },
  async getAllRawJobPictures(pics: IdProvider[]): Promise<Picture[]> {
    return (
      await Promise.all(pics.map((pic) => queries.getRawJobPicture(pic)))
    ).filter((pic) => !!pic) as Picture[];
  },
};

const mutations = {
  async createJobPictures(job: IdProvider, images: ImageType[]): Promise<void> {
    if (!job?.id) {
      return;
    }
    const formData = new FormData();
    images.forEach((image, index) => {
      if (!image.file) {
        return;
      }
      formData.append(`image_${index}`, image.file);
    });
    const res = await (axios({
      method: 'post',
      data: formData,
      url: `/images/job/add/${job.id}/`,
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as AxiosPromise<void>);
    return unwrap(res);
  },
  async removeJobPictures(images: IdProvider[]): Promise<void> {
    await Promise.all(
      images.map(
        (image) => image?.id && axios.delete(`/images/job/${image.id}/`),
      ),
    );
  },
  async acceptJobPicture(pic: IdProvider): Promise<void> {
    if (!pic?.id) {
      return;
    }
    return axios.patch<void>(`/job/pic/${pic.id}/accept/`).then(unwrap);
  },
  async rotatePicture(pic: IdProvider, rotation: 0 | 90 | 180 | 270) {
    if (!pic?.id) {
      return null;
    }
    const res = await axios.put<string>(`/images/job/${pic.id}/`, { rotation });
    return unwrap(res);
  },
};

const hooks = {
  usePicturesOfJobQuery(
    job: IdProvider,
    options?: UseQueryOptions<JobPicture[] | null>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobPictureKeys.JOB_PICTURE, job?.id],
      queryFn: () => queries.getPicturesOfJob(job),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () =>
        queryClient.invalidateQueries([JobPictureKeys.JOB_PICTURE, job?.id]),
    ];
    return QueryResultAndInvalidate;
  },
  useUnacceptedPicturesCount(options?: UseQueryOptions<{ count: number }>) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobPictureKeys.UNACCEPTED_COUNT],
      queryFn: queries.getUnacceptedPictureCount,
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([JobPictureKeys.UNACCEPTED_COUNT]),
    ];
    return QueryResultAndInvalidate;
  },
  useJobsWithUnacceptedPictures(
    page: number,
    filterState: { employee?: number; project?: number },
    options?: UseQueryOptions<MissingPicJob[]>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobPictureKeys.UNACCEPTED_PICTURE_JOBS, page, filterState],
      queryFn: () => queries.getJobsWithUnacceptedPictures(page, filterState),
      ...(options ?? {}),
    });
    const invalidate = () =>
      queryClient.invalidateQueries([JobPictureKeys.UNACCEPTED_PICTURE_JOBS]);
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      invalidate,
    ];
    return QueryResultAndInvalidate;
  },
  useAllRawJobPictures(
    pics: IdProvider[],
    options?: UseQueryOptions<Picture[]>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobPictureKeys.RAW_JOB_PICTURES, pics?.map((pic) => pic?.id)],
      queryFn: () => queries.getAllRawJobPictures(pics),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () =>
        queryClient.invalidateQueries([
          JobPictureKeys.RAW_JOB_PICTURES,
          pics?.map((pic) => pic?.id),
        ]),
    ];
    return QueryResultAndInvalidate;
  },
  useCreateJobPicturesMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ job, images }: { job: Job; images: ImageType[] }) =>
        mutations.createJobPictures(job, images),

      async onSuccess(_, { job }) {
        await queryClient.invalidateQueries([
          JobPictureKeys.JOB_PICTURE,
          job?.id,
        ]);
      },
    });
  },
  useRemoveJobPicturesMutation(job: IdProvider) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.removeJobPictures,
      async onSuccess() {
        await queryClient.invalidateQueries([
          JobPictureKeys.JOB_PICTURE,
          job?.id,
        ]);
        await queryClient.invalidateQueries([JobPictureKeys.UNACCEPTED_COUNT]);
        await queryClient.invalidateQueries([
          JobPictureKeys.UNACCEPTED_PICTURE_JOBS,
        ]);
      },
    });
  },
  useAcceptJobPicture(job: IdProvider) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.acceptJobPicture,
      onMutate(param) {
        if (!param?.id) {
          return;
        }
        const { id } = param;
        const jobPics: JobPicture[] | undefined = queryClient.getQueryData([
          JobPictureKeys.JOB_PICTURE,
          job?.id,
        ]);
        queryClient.setQueryData(
          [JobPictureKeys.JOB_PICTURE, job?.id],
          jobPics?.map((pic) =>
            pic.id !== id ? pic : { ...pic, accepted: true },
          ) ?? [],
        );
        return jobPics;
      },
      onError(_err, _vars, jobPics: JobPicture[] | undefined) {
        queryClient.setQueryData(
          [JobPictureKeys.JOB_PICTURE, job?.id],
          jobPics ?? [],
        );
      },
      async onSuccess() {
        await queryClient.invalidateQueries([
          JobPictureKeys.JOB_PICTURE,
          job?.id,
        ]);
        await queryClient.invalidateQueries([JobPictureKeys.UNACCEPTED_COUNT]);
        await queryClient.invalidateQueries([
          JobPictureKeys.UNACCEPTED_PICTURE_JOBS,
        ]);
      },
    });
  },
  useRotatePicture(
    job: IdProvider,
    manualRefetchCallback?: (id: string) => void | Promise<unknown>,
  ) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({
        id,
        rotation,
      }: IdProvider & { rotation: 0 | 90 | 180 | 270 }) =>
        mutations.rotatePicture({ id }, rotation),

      async onSuccess(_, { id }) {
        await queryClient.invalidateQueries([
          JobPictureKeys.JOB_PICTURE,
          job?.id,
        ]);
        await queryClient.invalidateQueries([
          JobPictureKeys.UNACCEPTED_PICTURE_JOBS,
        ]);
        if (manualRefetchCallback) {
          await manualRefetchCallback(String(id));
        }
      },
    });
  },
};

const JobPictureApi = {
  queries,
  mutations,
  hooks,
};

export default JobPictureApi;
